//@flow
import React from 'react';
import Link from './../../../components/Link';
import CustomerModel from '../../Stores/Customer/customerModel';
import OutOfStockReminder from '../../ItemQty/components/OutOfStockReminder.react';
import MsrpProductPrices from './MsrpProductPrices.react';
import ProductReviewSummary from '../../../components/ProductReviewSummary';
import NumberUtils from '../../Utils/number';

import type { List } from 'immutable';
import type ProductModel from '../../Stores/Products/productModel';
import type QuoteModel from '../../Stores/Quote/quoteModel';
import type CategoriesCollection from '../../Stores/MemberCategories/categoriesCollection';
import type QuoteTotalsModel from '../../Stores/Quote/quoteTotalsModel';
import type MembershipPlanModel from '../../Stores/Membership/membershipPlanModel';

type Props = {
  children?:React$Node,
  className:string,
  quote:QuoteModel,
  quoteTotals?:QuoteTotalsModel,
  showPrice:boolean,
  inStock?:boolean,
  preSale?:boolean,
  customer?:CustomerModel,
  showOOSView?:'product'|'tooltip',
  stockReminders?:List<string>,
  notAllowedReasons?:?string[],
  linkToProduct?:boolean,
  product:ProductModel,
  categories:CategoriesCollection,
  hideBadge?:boolean,
  preHeading?:?string,
  hideSize?:boolean,
  showBasicPrice?:boolean,
  membershipPlan:MembershipPlanModel,
  isOffice?:boolean,
  hideReviews?:boolean,
};

export default class ProductBasicInfo extends React.Component<Props> {
  static defaultProps = {
    showPrice:true,
  };

  _isGift():boolean {
    const giftCategory = this.props.categories.findByUrlKey('m-gifts');
    if (!giftCategory) return false;
    return this.props.product.isInCategory(giftCategory);
  }

  _getNotAllowedReasons() {
    const notAllowedReasons = this.props.notAllowedReasons;
    if (!notAllowedReasons) return;

    const className = 'not-allowed-reasons';
    if (notAllowedReasons.length === 1) {
      return <div className={className}>{notAllowedReasons[0]}</div>;
    }

    const reasons = notAllowedReasons.map((reason, i) => {
      return <li key={i}>{reason}</li>;
    });

    return <ul className={className}>{reasons}</ul>;
  }

  _getNotice() {
    if (this.props.notAllowedReasons) {
      return this._getNotAllowedReasons();
    }

    const sku = this.props.product.getSku();
    const comingSoon = this.props.categories.productIsComingSoon(sku);

    if ((!this.props.inStock || comingSoon) && this.props.showPrice) {
      return (
        <OutOfStockReminder
          sku={sku}
          customer={this.props.customer}
          showView={this.props.showOOSView}
          stockReminders={this.props.stockReminders}
          comingSoon={this.props.categories.productIsComingSoon(sku)}
        />
      );
    }

    if (this.props.preSale) {
      return (
        <div className="presale-copy">
          This item is launching soon! We have limited quantities available. Pre-order today to reserve yours and we’ll ship it as soon as it’s available.
        </div>
      );
    }

    return null;
  }

  _getBasicPrice() {
    const price = this.props.quote.getCustomerGroup() === 'MSRP' ? this.props.product.getMsrp() :
      this.props.product.getMemberPrice();
    return (
      <div className="prices-container group">
        <div className="prices">
          <span className="bold discount-price text-3">${price}</span>
        </div>
      </div>
    );
  }

  _getPrice() {
    if (!this.props.showPrice) return null;
    if (this.props.showBasicPrice) return this._getBasicPrice();

    return (
      <MsrpProductPrices
        product={this.props.product}
        quote={this.props.quote}
        hideMemInfo={this._isGift() || this.props.isOffice}
        membershipPlan={this.props.membershipPlan}
        isOffice={this.props.isOffice}
      />
    );
  }

  _getSize() {
    const size = NumberUtils.getWeightOz(this.props.product.getWeight());

    if (size && !this.props.hideSize) {
      return <span className="size">{size} oz</span>;
    }
  }

  _getProductHeader() {
    let content = <h1>{this.props.product.getName()}</h1>;
    if (this.props.linkToProduct) {
      content = <Link to={this.props.product.getUrl()} external={true}>{content}</Link>;
    }
    if (this.props.preHeading) {
      return (
        <React.Fragment>
          <h2 className="product-basic-info-preheading">
            {this.props.preHeading}
          </h2>
          {content}
        </React.Fragment>
      );
    }
    else {
      return content;
    }
  }

  _isNew() {
    const productId = this.props.product.getId();
    return this.props.categories && this.props.categories.productIsNew(productId);
  }

  _getMemberPrice() {
    return Number(this.props.product.getMemberPrice());
  }

  _getBadge() {
    const sku = this.props.product.getSku();
    const comingSoon = this.props.categories.productIsComingSoon(sku);

    if (this.props.hideBadge) return;

    const minFreeShipping = this.props.quoteTotals && this.props.quoteTotals.getMinimumFreeShipping();
    const hasFreeShipping = minFreeShipping && (this._getMemberPrice() >= minFreeShipping);
    if (hasFreeShipping) {
      return <div className="snack-badge free-shipping">FREE SHIPPING</div>;
    }
    if (this._isNew()) {
      return <div className="snack-badge new">NEW</div>;
    }
    if (this.props.preSale && !comingSoon) {
      return <div className="snack-badge pre-sale">PRE-ORDER-NOW</div>;
    }
  }

  _getReviews() {
    if (this.props.hideReviews) return null;
    return (
      <div className="product-basic-info-reviews-summary">
        <ProductReviewSummary
          product={this.props.product}
        />
      </div>
    );
  }

  render() {
    return(
      <div className={this.props.className}>
        {this._getBadge()}
        {this._getProductHeader()}
        <div className="detail-holder">
          <div className="size-price">
            <div className="size-container">
              {this._getSize()}
            </div>
            {this._getPrice()}
          </div>
          {this._getReviews()}
          <div className="button-detail-holder">
            {this.props.children}
            {this._getNotice()}
          </div>
        </div>
      </div>
    );
  }
}
